/**
 * @file ヘッダー右のメニュー (ハンバーガー)。
 *
 * @what: ボタンは三本線のアイコンだけを出し、開くと家族名・役割・アカウント名と、
 *   「家族を変える」(所属が複数のとき)・「アプリを更新する」・「ログアウト」を並べる。開くと
 *   最初の項目に焦点が移り、↑↓ で項目を移動、Esc と外側のクリックで閉じる
 *   (Esc はボタンへ焦点を戻す)。
 * @why: ヘッダーのボタンに家族名を出すと、名前が未設定の家庭では識別子めいた文字列が
 *   常時見えてしまう (代表 2026-08-24)。畳んだ中身は変えず、入口だけ一般的な
 *   ハンバーガーにする。
 * @failure: 描画のみ。ログアウトの失敗は firebaseAuth 側の扱いに従う。
 */

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { HeaderFamily } from "./headerContext";
import { ThemeSwitch } from "./ThemeSwitch";
import type { ThemeMode } from "./theme";
import { reloadLatestApp } from "./appUpdate";

export function AccountMenu({
  family,
  accountLabel,
  onSignOut,
  theme,
}: {
  family: HeaderFamily | null;
  /** 家族が決まっていないときにボタンへ出す名前 (表示名かメール)。 */
  accountLabel: string;
  onSignOut: () => void;
  /** 見た目の切り替えを出す面 (いちごスタディ) でだけ渡す。ほかの面では null。 */
  theme: { mode: ThemeMode; setMode: (next: ThemeMode) => void } | null;
}) {
  const [open, setOpen] = useState(false);
  // 取り直しの間もメニューは開いたままにする。閉じると押した結果が画面から消え、
  // 効いたのかどうか分からなくなるためである。
  const [updating, setUpdating] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const items = () =>
    Array.from(
      rootRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"], [role="menuitemradio"]') ??
        [],
    );

  // 開いたら最初の項目へ焦点を移す。閉じたらボタンへ戻す (メニューボタンの作法)。
  useEffect(() => {
    if (!open) return;
    items()[0]?.focus();
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
        return;
      }
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      const list = items();
      if (list.length === 0) return;
      e.preventDefault();
      const at = list.findIndex((el) => el === document.activeElement);
      const next =
        e.key === "ArrowDown" ? (at + 1) % list.length : (at - 1 + list.length) % list.length;
      list[next]?.focus();
    };
    document.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="app-menu" ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        className="app-menu-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="メニュー"
        onClick={() => setOpen((o) => !o)}
      >
        <span aria-hidden="true" className="app-menu-bars">
          <span />
          <span />
          <span />
        </span>
      </button>
      {open ? (
        <div className="app-menu-list" role="menu" aria-label="アカウント">
          <div className="app-menu-head">
            {family ? (
              <>
                <strong>{family.label}</strong>
                <span>あなたの役割: {family.role}</span>
                {accountLabel ? <span className="app-menu-sub">{accountLabel}</span> : null}
              </>
            ) : (
              <strong>{accountLabel}</strong>
            )}
          </div>
          {family?.switchable ? (
            <Link
              role="menuitem"
              className="app-menu-item"
              to="/study"
              onClick={() => setOpen(false)}
            >
              家族を変える
            </Link>
          ) : null}
          {theme ? <ThemeSwitch mode={theme.mode} onChange={theme.setMode} /> : null}
          <button
            role="menuitem"
            type="button"
            className="app-menu-item"
            aria-busy={updating}
            onClick={() => {
              if (updating) return;
              setUpdating(true);
              void reloadLatestApp();
            }}
          >
            {updating ? "更新しています…" : "アプリを更新する"}
          </button>
          <button
            role="menuitem"
            type="button"
            className="app-menu-item"
            onClick={() => {
              setOpen(false);
              onSignOut();
            }}
          >
            ログアウト
          </button>
        </div>
      ) : null}
    </div>
  );
}
